import type { ReactNode } from 'react';
import { X, type LucideIcon } from 'lucide-react';
import { useLanguage } from '../../../../../contexts/LanguageContext';

type IconColor = 'primary' | 'green' | 'amber' | 'red';
type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  icon?: LucideIcon;
  iconColor?: IconColor;
  size?: ModalSize;
  children: ReactNode;
}

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
};

const iconColorClasses: Record<IconColor, string> = {
  primary: 'bg-primary-100 text-primary-600',
  green: 'bg-green-100 text-green-600',
  amber: 'bg-amber-100 text-amber-600',
  red: 'bg-red-100 text-red-600',
};

export function Modal({
  isOpen,
  onClose,
  title,
  icon: Icon,
  iconColor = 'primary',
  size = 'md',
  children,
}: ModalProps) {
  const { t } = useLanguage();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop with blur */}
      <button
        type="button"
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity animate-fade-in cursor-default"
        onClick={onClose}
        aria-label={t('common.closeModal', 'Close modal')}
      />

      {/* Modal container */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className={`relative bg-white rounded-2xl shadow-2xl ${sizeClasses[size]} w-full animate-scale-up`}
        >
          {/* Header */}
          <div className="flex items-center gap-4 p-6 border-b border-gray-100">
            {Icon && (
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${iconColorClasses[iconColor]}`}>
                <Icon className="w-6 h-6" />
              </div>
            )}
            <h2 className="text-xl font-bold text-gray-900 flex-1">{title}</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

// Footer component for consistent button layout
interface ModalFooterProps {
  children: ReactNode;
}

export function ModalFooter({ children }: ModalFooterProps) {
  return (
    <div className="flex justify-end gap-3 mt-6">
      {children}
    </div>
  );
}
